import React, { useState, useEffect } from "react";
import { useCompany } from "../Context/CompanyContext"; // Importar el contexto de la empresa
import { fetchWithAuth, useUser } from "../Context/UserContext";
import { useNavigate } from "react-router-dom";
import API_URL from "../config";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const ROLES = ["Administrador", "Comprador", "Aprobador", "Lector"];

const CompanyUsers = () => {
  const { companies } = useCompany(); // Acceder al contexto de la empresa
  const { user } = useUser();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]); // Estado para almacenar los usuarios
  const id_company = companies?.company?.id_company; // Obtener el ID de la empresa desde el contexto
  const isAdmin = companies?.company?.tipo_cuenta === "Administrador";
  const [puesto, setPuesto] = useState("");
  const [area, setArea] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const current_user_id = localStorage.getItem("current_user_id");

  // Función para obtener los usuarios de la empresa
  const fetchUsers = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company/${id_company}/users`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data); // Almacenar los usuarios
      } else {
        throw new Error("Error al obtener los usuarios.");
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  // Redirigir al login si no hay usuario autenticado
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user]);

  useEffect(() => {
    if (current_user_id) {
      fetchUsers(); // Llamar a la función para obtener los usuarios si hay una empresa cargada
    }
  }, []);

  const updateUserPosition = async (puesto, area) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company/${id_company}/update_user_position`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ puesto, area }),
        }
      );
      if (response.ok) {
        fetchUsers(); // Recargar la lista de usuarios
        toast.success("Puesto y área actualizados correctamente.");
      } else {
        const data = await response.json();
        toast.error(data.error || "Error al actualizar puesto y área.");
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  // Función para aceptar usuario
  const acceptUser = async (userId) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company/${id_company}/accept_user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId }),
        }
      );
      if (response.ok) {
        fetchUsers(); // Recargar la lista de usuarios
        toast.success("Usuario aceptado con éxito.");
      } else {
        const data = await response.json();
        toast.error(data.error || "Error al aceptar al usuario.");
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  // Función para eliminar usuario
  const removeUser = async (userId) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company/${id_company}/remove_user`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId }),
        }
      );
      if (response.ok) {
        fetchUsers(); // Recargar la lista de usuarios
        toast.success("Usuario eliminado con éxito.");
      } else {
        const data = await response.json();
        toast.error(data.error || "Error al eliminar al usuario.");
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  // Función para cambiar el rol del usuario
  const changeRole = async (userId, role) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company/${id_company}/set_role`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId, role }),
        }
      );
      if (response.ok) {
        fetchUsers(); // Recargar la lista de usuarios
        toast.success("Rol del usuario cambiado con éxito.");
      } else {
        const data = await response.json();
        toast.error(data.error || "Error al cambiar el rol del usuario.");
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-foreground">Administrar Usuarios</h3>
      <p className="text-sm text-muted-foreground">Revisa y administra los usuarios de la empresa</p>

      <div className="mt-4 space-y-4">
        {users.map((u, index) => (
          <Card key={index}>
            <CardContent className="flex gap-4 pt-6">
              <img src="/fotos/persona.png" alt="Icono de usuario" className="h-12 w-12 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1 text-sm">
                <p>
                  <strong className="text-foreground">
                    <a
                      href={`/profile/${u.id_user}/${encodeURIComponent(
                        `${u.nombre}-${u.apellido}`.replace(/\s+/g, "-")
                      )}`}
                      className="hover:text-primary"
                    >
                      {u.nombre} {u.apellido}
                    </a>
                  </strong>
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Correo: </strong>
                  {u.correo}
                </p>

                {u.id_user === parseInt(current_user_id) && isEditing ? (
                  <>
                    <div className="flex items-center gap-2">
                      <strong className="text-foreground">Puesto:</strong>
                      <Input
                        type="text"
                        className="h-8"
                        value={puesto}
                        onChange={(e) => setPuesto(e.target.value)}
                        placeholder={u.puesto}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <strong className="text-foreground">Área:</strong>
                      <Input
                        type="text"
                        className="h-8"
                        value={area}
                        onChange={(e) => setArea(e.target.value)}
                        placeholder={u.area}
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        onClick={() => {
                          updateUserPosition(puesto, area);
                          setIsEditing(false);
                        }}
                      >
                        Guardar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Puesto: </strong> {u.puesto}
                    </p>
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Área: </strong> {u.area}
                    </p>
                  </>
                )}

                <p className="text-muted-foreground">
                  <strong className="text-foreground">Tipo de cuenta: </strong>
                  {u.tipo_cuenta}
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Estado:</strong> {u.estado_usuario}
                </p>

                {u.id_user === parseInt(current_user_id) && !isEditing && (
                  <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                    Editar
                  </Button>
                )}

                {u.id_user !== parseInt(current_user_id) && isAdmin && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {u.estado_usuario === "pendiente" && (
                      <Button size="sm" onClick={() => acceptUser(u.id_user)}>
                        Activar
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => removeUser(u.id_user)}>
                      Eliminar
                    </Button>
                    <select
                      className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
                      value={u.tipo_cuenta}
                      onChange={(e) => changeRole(u.id_user, e.target.value)}
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CompanyUsers;
